import { Directive, ElementRef, HostListener } from '@angular/core';

const NAV_CELL_CLASS = 'grid-nav-cell';
const ROW_TOLERANCE_PX = 4;

type ArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

/**
 * Roving keyboard navigation for a grid of focusable `.grid-nav-cell` elements.
 * Applied to a table/pane container; arrow keys move focus geometrically and
 * Ctrl/Cmd+Home/End jump to the first/last cell. Works with recursive and
 * collapsible tables because neighbours are resolved from the live DOM.
 */
@Directive({
  selector: '[appGridNav]',
  standalone: false,
})
export class GridNavigationDirective {
  constructor(private host: ElementRef<HTMLElement>) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const current = event.target as HTMLElement;
    if (!current?.classList?.contains(NAV_CELL_CLASS)) {
      return;
    }

    const isEdgeKey = (event.key === 'Home' || event.key === 'End') && (event.ctrlKey || event.metaKey);
    const isArrowKey = this.isArrowKey(event.key);
    if (!isEdgeKey && !isArrowKey) {
      return;
    }

    const cells = this.visibleCells();
    if (!cells.length) {
      return;
    }

    let next: HTMLElement | null;
    if (event.key === 'Home') {
      next = cells[0];
    } else if (event.key === 'End') {
      next = cells[cells.length - 1];
    } else {
      next = this.findNeighbor(current, cells, event.key as ArrowKey);
    }

    if (next) {
      event.preventDefault();
      event.stopPropagation();
      next.focus();
    }
  }

  private isArrowKey(key: string): key is ArrowKey {
    return key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';
  }

  private visibleCells(): HTMLElement[] {
    const nodes = this.host.nativeElement.querySelectorAll<HTMLElement>('.' + NAV_CELL_CLASS);
    return Array.from(nodes).filter(el => el.offsetParent !== null);
  }

  private findNeighbor(current: HTMLElement, cells: HTMLElement[], key: ArrowKey): HTMLElement | null {
    const from = current.getBoundingClientRect();
    let best: HTMLElement | null = null;
    let bestScore = Infinity;

    for (const cell of cells) {
      if (cell === current) {
        continue;
      }
      const rect = cell.getBoundingClientRect();
      const sameRow = Math.abs(rect.top - from.top) < ROW_TOLERANCE_PX;
      let score = Infinity;

      if (key === 'ArrowRight' && sameRow && rect.left > from.left) {
        score = rect.left - from.left;
      } else if (key === 'ArrowLeft' && sameRow && rect.left < from.left) {
        score = from.left - rect.left;
      } else if (key === 'ArrowDown' && rect.top > from.top + ROW_TOLERANCE_PX) {
        score = (rect.top - from.top) + Math.abs(rect.left - from.left);
      } else if (key === 'ArrowUp' && rect.top < from.top - ROW_TOLERANCE_PX) {
        score = (from.top - rect.top) + Math.abs(rect.left - from.left);
      }

      if (score < bestScore) {
        bestScore = score;
        best = cell;
      }
    }

    return best;
  }
}
